/**
 * Shell top nav (spec S1): mega-menu (Features | Models two-column panels
 * fed by the registry), credits chip, language globe, theme toggle, auth.
 * RTL-safe by construction — logical properties only.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink, useNavigate } from 'react-router';
import { useAuth } from '../lib/auth';
import { usePrefs } from '../stores/prefs';
import { CreditChip } from './commerce';
import { Globe, LogOut, Menu, Moon, Settings, Sun, X } from './icons';
import { Logo } from './Logo';
import { MegaPanel } from './MegaMenu';
import { NotificationsBell } from './NotificationsBell';
import { Badge, Button, IconButton } from './ui';

interface MegaItem {
  labelKey: string;
  href: string;
  modality?: 'image' | 'video' | 'audio';
  soon?: boolean;
  isNew?: boolean;
}

// Nav surface map. Studios beyond P0 route to the pricing/roadmap teaser and
// are marked "soon" — honest scope, real links the moment each epic lands.
const NAV_ITEMS: MegaItem[] = [
  { labelKey: 'nav.explore', href: '/explore' },
  { labelKey: 'nav.templates', href: '/templates', isNew: true },
  { labelKey: 'nav.image', href: '/create/image', modality: 'image' },
  { labelKey: 'nav.video', href: '/create/video', modality: 'video' },
  { labelKey: 'nav.audio', href: '/create/audio', modality: 'audio' },
  { labelKey: 'nav.marketingStudio', href: '/studio/marketing' },
  { labelKey: 'nav.cinemaStudio', href: '/studio/cinema' },
  { labelKey: 'nav.mcpCli', href: '/mcp' },
  { labelKey: 'nav.supercomputer', href: '/supercomputer', isNew: true },
  { labelKey: 'nav.canvas', href: '/soon/canvas', soon: true },
  { labelKey: 'nav.shortsStudio', href: '/studio/faceless' },
  { labelKey: 'nav.pricing', href: '/pricing' },
];

export function Navbar() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { theme, setTheme, locale, setLocale } = usePrefs();
  // Panel state carries its anchor x: the items row is an overflow-x scroll
  // container, which CLIPS absolutely-positioned children — so the single
  // panel renders OUTSIDE it (fixed under the sticky header), anchored to
  // the trigger's inline-start edge (RTL-aware, clamped to the viewport).
  const [openMenu, setOpenMenu] = useState<{ key: string; modality: 'image' | 'video' | 'audio'; href: string; x: number } | null>(null);
  const anchorX = (el: HTMLElement): number => {
    const rect = el.getBoundingClientRect();
    const rtl = document.documentElement.dir === 'rtl';
    const start = rtl ? window.innerWidth - rect.right : rect.left;
    return Math.max(8, Math.min(start, window.innerWidth - 676));
  };
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  // Mega menu closes on Escape + outside pointerdown — hover alone locked
  // out keyboard/touch users (a11y audit fix).
  useEffect(() => {
    if (!openMenu) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpenMenu(null);
    };
    const onPointer = (e: PointerEvent) => {
      const t = e.target as HTMLElement;
      if (!t.closest('[role="menu"]') && !t.closest('nav')) setOpenMenu(null);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointer);
    };
  }, [openMenu]);

  return (
    <header className="sticky top-0 z-40 border-b border-border bg-bg/85 backdrop-blur-md">
      <nav className="mx-auto flex h-14 max-w-[1500px] items-center gap-1 px-4">
        {/* Brand */}
        <Link to="/" className="me-3 flex shrink-0 items-center" aria-label={t('brand')}>
          <Logo />
        </Link>

        {/* Mega menu (desktop) */}
        <div className="hidden flex-1 items-center gap-0.5 overflow-x-auto rail lg:flex">
          {NAV_ITEMS.map((item) => (
            <div
              key={item.labelKey}
              className="relative"
              onMouseEnter={(e) => item.modality && setOpenMenu({ key: item.labelKey, modality: item.modality, href: item.href, x: anchorX(e.currentTarget) })}
            >
              <NavLink
                to={item.href}
                aria-haspopup={item.modality ? 'menu' : undefined}
                aria-expanded={item.modality ? openMenu?.key === item.labelKey : undefined}
                onClick={(e) => {
                  // First activation on touch/keyboard opens the panel instead
                  // of navigating; a second click follows the link.
                  if (item.modality && openMenu?.key !== item.labelKey) {
                    e.preventDefault();
                    setOpenMenu({ key: item.labelKey, modality: item.modality, href: item.href, x: anchorX(e.currentTarget) });
                  } else {
                    setOpenMenu(null);
                  }
                }}
                className={({ isActive }) =>
                  `flex items-center gap-1 whitespace-nowrap rounded-lg px-2.5 py-1.5 text-[13px] transition-colors ${
                    isActive ? 'text-accent' : item.soon ? 'text-muted/70 hover:text-muted' : 'text-muted hover:text-text'
                  }`
                }
              >
                {t(item.labelKey)}
                {item.isNew && <Badge tone="accent">{t('nav.new')}</Badge>}
              </NavLink>

            </div>
          ))}
        </div>
        <div className="flex-1 lg:hidden" />

        {/* Right cluster */}
        <div className="flex shrink-0 items-center gap-2">
          <CreditChip />
          {user && <NotificationsBell />}
          {/* Language toggle — shows the language you'd switch TO. */}
          <button
            onClick={() => setLocale(locale === 'en' ? 'ar' : 'en')}
            className="flex items-center gap-1.5 rounded-full px-2.5 py-1.5 text-xs font-medium text-muted transition-colors hover:bg-surface-2 hover:text-text"
            title={t('common.language')}
          >
            <Globe className="size-4" aria-hidden />
            <span>{locale === 'en' ? 'عربي' : 'EN'}</span>
          </button>
          {/* Theme toggle */}
          <IconButton
            label={theme === 'dark' ? t('account.themeLight') : t('account.themeDark')}
            onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
          >
            {theme === 'dark' ? <Sun className="size-4" /> : <Moon className="size-4" />}
          </IconButton>

          {user ? (
            <div className="flex items-center gap-1.5">
              {user.role === 'admin' && (
                <Link
                  to="/admin"
                  aria-label={t('admin.title')}
                  title={t('admin.title')}
                  className="hidden size-9 items-center justify-center rounded-full text-muted transition-colors hover:bg-surface-2 hover:text-text focus-visible:outline-2 focus-visible:outline-accent sm:inline-flex"
                >
                  <Settings className="size-4" aria-hidden />
                </Link>
              )}
              <Link to="/library" className="hidden rounded-lg px-2.5 py-1.5 text-[13px] text-muted hover:text-text sm:block">
                {t('nav.library')}
              </Link>
              <Link
                to="/account"
                className="flex size-8 items-center justify-center rounded-full bg-accent/15 text-xs font-bold uppercase text-accent ring-1 ring-accent/30 transition-shadow hover:ring-accent/60"
              >
                {user.displayName.slice(0, 1)}
              </Link>
              <IconButton label={t('nav.logout')} onClick={() => void logout().then(() => navigate('/'))} className="hidden sm:inline-flex">
                <LogOut className="size-4 icon-mirror" />
              </IconButton>
            </div>
          ) : (
            <>
              <Link to="/login" className="rounded-lg px-2.5 py-1.5 text-[13px] text-muted hover:text-text">
                {t('nav.login')}
              </Link>
              <Link to="/signup">
                <Button variant="accent" size="sm">{t('nav.signup')}</Button>
              </Link>
            </>
          )}

          {/* Mobile menu */}
          <IconButton label={t('nav.menu')} className="lg:hidden" onClick={() => setMobileOpen(!mobileOpen)}>
            {mobileOpen ? <X className="size-5" /> : <Menu className="size-5" />}
          </IconButton>
        </div>
      </nav>

      {openMenu && (
        <div
          className="fixed top-14 z-50"
          style={{ insetInlineStart: openMenu.x }}
          onMouseLeave={() => setOpenMenu(null)}
        >
          <MegaPanel modality={openMenu.modality} href={openMenu.href} onNavigate={() => setOpenMenu(null)} />
        </div>
      )}

      {mobileOpen && (
        <div className="border-t border-border bg-surface px-4 py-3 lg:hidden">
          <div className="grid grid-cols-2 gap-1">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.labelKey}
                to={item.href}
                onClick={() => setMobileOpen(false)}
                className="rounded-lg px-3 py-2 text-sm text-muted hover:bg-surface-2 hover:text-text"
              >
                {t(item.labelKey)}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}
